import { useMutation } from "@apollo/client";
import {
  Button,
  Center,
  Checkbox,
  HStack,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Spinner,
  Stack,
  Text,
} from "@chakra-ui/react";
import {
  InternalAdminDistributionGroupFragment,
  InternalAdminEditDistributionGroupMutation,
  InternalAdminEditDistributionGroupMutationVariables,
  InternalAdminGrantHistoricalAccessMutation,
  InternalAdminGrantHistoricalAccessMutationVariables,
} from "gql/graphql";
import React, { memo, useCallback, useMemo, useState } from "react";
import { useOverlay } from "modules/providers/overlay-provider";
import colors from "app/colors";
import useInternalAdminDistributionGroupList from "../hooks/use-internal-admin-distribution-group-list";
import useQueryString from "modules/hooks/use-query-string";
import INTERNAL_ADMIN_EDIT_DISTRIBUTION_GROUP_MUTATION from "../_networking/internal-admin-edit-distribution-group";
import INTERNAL_ADMIN_GRANT_HISTORICAL_ACCESS_MUTATION from "../_networking/internal-admin-grant-historical-access";
import useInternalAdminUsers from "../hooks/use-internal-admin-users";
import InternalAdminDistributionGroupSelect from "../components/internal-admin-distribution-group-select";

interface IProps {
  userIds: string[];
}

const InternalAdminAddUsersToDistributionGroupsModal = ({
  userIds,
}: IProps) => {
  const { closeModal } = useOverlay();
  const { qsOrganizationId } = useQueryString();

  const { distributionGroups } = useInternalAdminDistributionGroupList({
    organizationId: qsOrganizationId,
    input: {
      limit: 1000000,
      offset: 0,
    },
  });

  const { users } = useInternalAdminUsers(userIds);

  const [_distributionGroups, setDistributionGroups] = useState<
    InternalAdminDistributionGroupFragment[]
  >([]);
  const [isHistoricalAccess, setIsHistoricalAccess] = useState(false);

  const excludeDistributionGroupIds = useMemo(() => {
    if (userIds.length === 1) {
      const userId = userIds[0];
      return distributionGroups
        .filter((distributionGroup) =>
          distributionGroup.userIdList.includes(userId),
        )
        .map((distributionGroup) => distributionGroup.id);
    }
    return [];
  }, [distributionGroups, userIds]);

  const [
    internalAdminEditDistributionGroupMutationAPI,
    { loading: isSubmitting },
  ] = useMutation<
    InternalAdminEditDistributionGroupMutation,
    InternalAdminEditDistributionGroupMutationVariables
  >(INTERNAL_ADMIN_EDIT_DISTRIBUTION_GROUP_MUTATION);

  const [
    internalAdminGrantHistoricalAccessAPI,
    { loading: isGrantingHistoricalAccess },
  ] = useMutation<
    InternalAdminGrantHistoricalAccessMutation,
    InternalAdminGrantHistoricalAccessMutationVariables
  >(INTERNAL_ADMIN_GRANT_HISTORICAL_ACCESS_MUTATION);

  const onChange = useCallback(
    (value: InternalAdminDistributionGroupFragment[]) => {
      if (value) {
        setDistributionGroups(value);
      }
    },
    [],
  );

  const onSubmit = useCallback(async () => {
    if (!qsOrganizationId) {
      return;
    }
    await Promise.all(
      _distributionGroups.map((distributionGroup) =>
        internalAdminEditDistributionGroupMutationAPI({
          variables: {
            organizationId: qsOrganizationId,
            id: distributionGroup.id,
            input: {
              addedUserIdList: [
                ...distributionGroup.addedUserIdList,
                ...users.map((u) => u.id),
              ],
            },
          },
        }),
      ),
    );
    if (isHistoricalAccess) {
      await Promise.all(
        users.flatMap((user) =>
          _distributionGroups.map((distributionGroup) =>
            internalAdminGrantHistoricalAccessAPI({
              variables: {
                organizationId: qsOrganizationId,
                userId: user.id,
                distributionGroupId: distributionGroup.id,
              },
            }),
          ),
        ),
      );
    }

    closeModal();
  }, [
    _distributionGroups,
    closeModal,
    internalAdminEditDistributionGroupMutationAPI,
    internalAdminGrantHistoricalAccessAPI,
    isHistoricalAccess,
    qsOrganizationId,
    users,
  ]);

  if (users.length === 0 || userIds.length !== users.length) {
    return null;
  }

  return (
    <Modal isOpen onClose={closeModal} size="xl" isCentered>
      <ModalOverlay />
      <ModalContent borderRadius={16}>
        <ModalBody p={8}>
          <Stack alignItems="flex-start" spacing={6} w="full">
            <Center w="full">
              <Text fontFamily="Outfit" fontWeight={700} fontSize={26}>
                Add User to Groups
              </Text>
            </Center>
            <Text
              textAlign="center"
              w="full"
              fontSize={16}
              color={colors.gray50}
              fontWeight={400}
            >
              {`Add ${users.length} user${users.length === 1 ? "" : "s"} to selected groups`}
            </Text>
            <InternalAdminDistributionGroupSelect
              onChange={onChange}
              excludeDistributionGroupIds={excludeDistributionGroupIds}
            />
            <HStack>
              <Checkbox
                isChecked={isHistoricalAccess}
                onChange={(e) => setIsHistoricalAccess(e.target.checked)}
                color={colors.gray50}
              />
              <Text fontSize={16} color={colors.gray50} fontWeight={400}>
                Allow user(s) access to all historical conversations in group(s)
              </Text>
            </HStack>
            <Center pt={5} w="full">
              <HStack spacing={8}>
                <Button
                  variant="cancel"
                  height={12}
                  w={28}
                  onClick={closeModal}
                >
                  Cancel
                </Button>
                <Button
                  height={12}
                  w={28}
                  onClick={onSubmit}
                  isDisabled={distributionGroups.length === 0 || isSubmitting}
                >
                  {isSubmitting || isGrantingHistoricalAccess ? (
                    <Spinner />
                  ) : (
                    "Save"
                  )}
                </Button>
              </HStack>
            </Center>
          </Stack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default memo(InternalAdminAddUsersToDistributionGroupsModal);
